import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Mail, Info, CreditCard, Building2 } from "lucide-react";
import { Itinerary } from "@/hooks/useItineraries";
import { SUPABASE_KEY, SUPABASE_URL } from "@/components/utils/constants";
import { createClient } from "@supabase/supabase-js";

interface SendQuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    itinerary: Itinerary;
    onSendQuote?: (stripeUrl: string, payment: string) => Promise<boolean>;
}

export const SendQuoteModal = ({
    isOpen,
    onClose,
    itinerary,
    onSendQuote
}: SendQuoteModalProps) => {
    // Payment method states
    const [useStripe, setUseStripe] = useState(true);
    const [stripeUrl, setStripeUrl] = useState<string | null>(null);
    const [stripeError, setStripeError] = useState(false);

    // Modal phase states
    const [isCreating, setIsCreating] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Fetch Stripe link when modal opens
    useEffect(() => {
        const fetchStripeLink = async () => {
            if (!itinerary) return;
            setStripeError(false);
            setStripeUrl(null);
            try {
                await createStripeLink(itinerary);
            } catch (error) {
                setStripeError(true);
            }
        };
        if (isOpen && itinerary) {
            fetchStripeLink();
        }
    }, [isOpen, itinerary]);

    // Send quote logic
    const handleSendQuote = async () => {
        if (!onSendQuote) return;

        // Check if Stripe is required but not available
        if (useStripe && !stripeUrl) return;

        setIsCreating(true);
        setHasError(false);
        try {
            const success = await onSendQuote(useStripe ? stripeUrl : null, getPaymentData());
            if (success) {
                setIsSuccess(true);
            } else {
                setHasError(true);
            }
        } catch (error) {
            setHasError(true);
        } finally {
            setIsCreating(false);
        }
    };

    // Get bank transfer details
    const getBankTransferDetails = () => {
        const customerName = `${itinerary?.contact_first_name || ''} ${itinerary?.contact_last_name || ''}`.trim();
        const year = new Date().getFullYear();

        return {
            method: 'bank_transfer',
            beneficiary: 'PLACE2BE S.R.L.',
            bank: 'Intesa Sanpaolo',
            iban: 'IT94M0306975823100000005869',
            swiftCode: 'BCITITMM',
            reference: `${customerName} Saldo ${itinerary.id} ${year}`,
        };
    };

    const getPaymentData = () => {
        const bankDetails = getBankTransferDetails();
        return `Recipient: <strong>${bankDetails.beneficiary}</strong><br>
            Bank: <strong>${bankDetails.bank}</strong><br>
            IBAN: <strong>${bankDetails.iban}</strong><br>
            SWIFT CODE: <strong>${bankDetails.swiftCode}</strong><br>
            Reference: <strong>${bankDetails.reference}</strong><br>`;
    };

    // Reset modal state
    const handleClose = () => {
        setIsSuccess(false);
        setIsCreating(false);
        setHasError(false);
        setStripeUrl(null);
        setStripeError(false);
        setUseStripe(true);
        onClose();
    };

    async function createStripeLink(itinerary: Itinerary) {
        const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);
        const obj = {
            line_items: [{
                price_data: {
                    currency: "eur",
                    product_data: {
                        name: itinerary.title,
                        description: "Place2Be Trip Quote",
                    },
                    // Converti in centesimi ed evita problemi di precisione floating-point
                    unit_amount: Math.round((Number(itinerary.total) || 0) * 100), // es. 271.78 -> 27178
                },
                quantity: 1
            }],
            metadata: {
                travel_request_id: itinerary.travel_request_id,
                user_id: itinerary.contact_email,
                itinerary_id: itinerary.id,
            }
        };

        const stripeLink = await supabase.functions.invoke('create-stripe-link', {
            body: obj,
        });
        if (stripeLink.error) throw new Error(stripeLink.error.message);
        const data = stripeLink.data as { url: string };
        if (data.url) setStripeUrl(data.url);
        else setStripeError(true);
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Send Travel Quote"
            size="lg"
            footer={
                !isSuccess ? (
                    <div className="flex items-center justify-between w-full">
                        <Button
                            variant="ghost"
                            onClick={handleClose}
                            disabled={isCreating}
                            className="px-6 py-3"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSendQuote}
                            disabled={isCreating || (useStripe && !stripeUrl)}
                            className="px-6 py-3 bg-black text-white hover:bg-gray-800 flex items-center gap-2"
                        >
                            {isCreating ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Mail className="h-4 w-4" />
                                    Send Quote
                                </>
                            )}
                        </Button>
                    </div>
                ) : (
                    <div className="flex justify-end w-full">
                        <Button onClick={handleClose} className="px-6 py-3 bg-black text-white hover:bg-gray-800">Close</Button>
                    </div>
                )
            }
        >
            <div className="py-6 px-6 space-y-6">
                {/* Customer Info */}
                {itinerary.travel_request_id && (
                    <div className="bg-gray-50 rounded-lg p-4 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                        <div className="text-sm text-gray-600">
                            <p><strong>Name:</strong> {itinerary.contact_first_name} {itinerary.contact_last_name}</p>
                            <p><strong>Email:</strong> {itinerary.contact_email}</p>
                            <p><strong>Trip:</strong> {itinerary.title}</p>
                            <p><strong>Dates:</strong> {itinerary.start_date} - {itinerary.end_date}</p>
                        </div>
                        <div className="text-sm text-gray-600">
                            <p><strong>Travel designer:</strong> {itinerary.td_name}  {itinerary.td_surname}</p>
                            <p><strong>Email:</strong> {itinerary.td_email}</p>
                        </div>
                    </div>
                )}

                {/* Payment Method Switch */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-3">
                            <CreditCard className="h-5 w-5 text-blue-600" />
                            <div>
                                <Label htmlFor="stripe-payment" className="text-sm font-medium">
                                    Stripe Payment Link
                                </Label>
                                <p className="text-xs text-gray-500">Secure online payment</p>
                            </div>
                        </div>
                        <Switch
                            id="stripe-payment"
                            checked={useStripe}
                            onCheckedChange={setUseStripe}
                            disabled={isCreating}
                        />
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-3">
                            <Building2 className="h-5 w-5 text-green-600" />
                            <div>
                                <Label htmlFor="bank-transfer" className="text-sm font-medium">
                                    Bank Transfer
                                </Label>
                                <p className="text-xs text-gray-500">Traditional bank transfer</p>
                            </div>
                        </div>
                        <Switch
                            id="bank-transfer"
                            checked={!useStripe}
                            onCheckedChange={(checked) => setUseStripe(!checked)}
                            disabled={isCreating}
                        />
                    </div>
                </div>

                {/* Payment Details */}
                {useStripe ? (
                    <div className="space-y-2">
                        {stripeError ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    Error creating Stripe payment link. Please try again.
                                </AlertDescription>
                            </Alert>
                        ) : stripeUrl ? (
                            <Alert variant="info">
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                    Stripe payment link created successfully.
                                    <a className="text-blue-500 underline ml-2" href={stripeUrl} target="_blank" rel="noopener noreferrer">
                                        View Payment Link
                                    </a>
                                </AlertDescription>
                            </Alert>
                        ) : (
                            <Alert variant="info">
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                    Creating Stripe payment link...
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Bank Transfer Details</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                            {(() => {
                                const bankDetails = getBankTransferDetails();
                                return (
                                    <>
                                        <p><strong>Recipient:</strong> {bankDetails.beneficiary}</p>
                                        <p><strong>Bank:</strong> {bankDetails.bank}</p>
                                        <p><strong>IBAN:</strong> {bankDetails.iban}</p>
                                        <p><strong>SWIFT CODE:</strong> {bankDetails.swiftCode}</p>
                                        <p><strong>Reference:</strong> {bankDetails.reference}</p>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* Info Alert */}
                <Alert variant="info">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                        A PDF document containing the complete travel itinerary will be automatically attached to this email.
                        {useStripe ? 'A Stripe payment link will be included.' : 'Bank transfer details will be included.'}
                    </AlertDescription>
                </Alert>
            </div>
        </Modal>
    );
};